// src/utils/api.js
// ---------------------------------------------------------------------------------
//  🛡️ Axios cliente con logging de seguridad por llamada.
// ---------------------------------------------------------------------------------
import axios from "axios";
import { msalInstance } from "./msalConfig";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { triggerGlobalError } from "../context/ErrorContext"; // ✅ nuevo


/* ----------------------------- Configuración ------------------------------------ */
const BASE_API_URL =
  process.env.REACT_APP_API_BASE 

/**
 * Si se define esta variable de entorno, los logs se enviarán vía POST (o sendBeacon)
 * a la URL indicada.  En caso contrario, se escriben sólo en consola.
 * Ej.:  REACT_APP_API_LOG_ENDPOINT="https://mi-servicio.logs/ingest"
 */
const LOG_ENDPOINT = process.env.REACT_APP_API_LOG_ENDPOINT;

/**
 * Envía (o imprime) la información de cada petición.
 * Un wrapper simple para que puedas sustituir por tu proveedor (Datadog, Sentry, etc.)
 */
function logApiCall({ method, url, status, duration, ok, error }) {
  const payload = {
    timestamp: new Date().toISOString(),
    method, /// GET, POST…
    url,
    status,
    duration, // milisegundos
    ok,
    error: error?.message ?? null
  };

  if (LOG_ENDPOINT) {
    // 👉 Solo consola (dev/local)
    // eslint-disable-next-line no-console
    console.info(
      `%c[API] %s %s → %s (%sms)`,
      ok ? "color: #4caf50" : "color:#f44336",
      method.toUpperCase(),
      url,
      status,
      duration
    );
    if (!ok && error) console.error(error);
    return;
  }

  try {
    const blob = new Blob([JSON.stringify(payload)], {
      type: "application/json"
    });
    // Preferimos beacon porque no bloquea navegación:
    if (navigator.sendBeacon) {
      navigator.sendBeacon(LOG_ENDPOINT, blob);
    } else {
      // Fallback fetch
      fetch(LOG_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      }).catch(() => {}); // no queremos hacer throw aquí
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("Error sending API log:", err);
  }
}

/* ----------------------- Instancia Axios con interceptores ---------------------- */
const apiClient = axios.create({
  baseURL: BASE_API_URL,
  headers: { Accept: "application/json" }
});

// ────────────────────────────────────────────────────────────────────────────────
// 1️⃣  Interceptor de REQUEST → añade token MSAL y metadata de performance
// ────────────────────────────────────────────────────────────────────────────────
apiClient.interceptors.request.use(async (config) => {
  // ⏱️ Marca de tiempo de inicio (performance.now tiene mayor precisión que Date)
  config.metadata = { startTime: performance.now ? performance.now() : Date.now() };

  try {
    const account = msalInstance.getActiveAccount();
    if (account) {
      try {
        const { accessToken } = await msalInstance.acquireTokenSilent({
          scopes: [
            "https://deepextractions.onmicrosoft.com/3c98d832-da8b-410d-889c-b8c769452a7c/Task.Write"
          ],
          account
        });
        /* eslint-disable no-param-reassign */
        config.headers.Authorization = `Bearer ${accessToken}`;
      } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
          await msalInstance.loginRedirect({
            scopes: [
              "https://deepextractions.onmicrosoft.com/3c98d832-da8b-410d-889c-b8c769452a7c/Task.Write"
            ]
          });
        } else {
          throw error;
        }
      }
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("Failed to obtain MSAL token:", err);
  }

  return config;
});

// ────────────────────────────────────────────────────────────────────────────────/
// 2️⃣  Interceptor de RESPONSE → calcula duración y registra el log (éxito o error)
// ────────────────────────────────────────────────────────────────────────────────
apiClient.interceptors.response.use(
  (response) => {
    const { config, status } = response;
    const end = performance.now ? performance.now() : Date.now();
    const duration = config.metadata ? end - config.metadata.startTime : null;

    logApiCall({
      method: config.method ?? "UNKNOWN",
      url: config.url ?? "",
      status,
      duration,
      ok: true
    });

    return response;
  },
  (error) => {
    const { config = {}, response = {} } = error;
    const status = response.status ?? "ERROR";
    const end = performance.now ? performance.now() : Date.now();
    const duration = config.metadata ? end - config.metadata.startTime : null;

    logApiCall({
      method: config.method ?? "UNKNOWN",
      url: config.url ?? "",
      status,
      duration,
      ok: false,
      error,
    });

    const displayableStatusCodes = [
      400, 401, 403, 404, 409, 413, 422, 429, 500, 503,
    ];

    if (displayableStatusCodes.includes(status)) {
      const msg =
        response.data?.error ||
        response.data?.message ||
        error.message ||
        "Unexpected error.";

      triggerGlobalError(msg, status); // ✅ pasa el código
    }


    return Promise.reject(error);
  }
);

/* -------------------------------------------------------------------------- */
/*                               Resto de funciones                           */
/* -------------------------------------------------------------------------- */

/* Obtener templates del usuario */
export async function getTemplatesAPI(limit = 50, before) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (before) params.append("before", before);

  try {
    const { data, status } = await apiClient.get(`/me/templates?${params}`);
    if (status === 200) return data; // { templates, has_more, message }
    throw new Error(data?.error || "Error retrieving the templates.");
  } catch (error) {
    throw new Error(
      error.response?.data?.error || "Error retrieving the templates."
    );
  }
}

/* Clonar template → tabla en proyecto */
export async function cloneTableFromTemplateAPI(
  projectId,
  clone_from_template_id,
  name
) {
  try {
    const body = { clone_from_template_id, name };
    const { data, status } = await apiClient.post(
      `/me/projects/${projectId}/tables/clone-from-template`,
      body,
      { headers: { "Content-Type": "application/json" } }
    );

    /* ✅ Éxito si status está en rango 2xx y viene la tabla */
    if (status >= 200 && status < 300 && data.table) return data.table;

    /* ⚠️ Respuesta inesperada: lanza con mensaje recibido */
    throw new Error(data?.error || data?.message || "Unknown error.");
  } catch (error) {
    const msg =
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      "Error cloning the template.";
    throw new Error(msg);
  }
}

/* -------------------------------------------------------------------------- */
/*                            NUEVO: crear template                           */
/* -------------------------------------------------------------------------- */
export async function createTemplateAPI({
  name,
  clone_from_project_id,
  clone_from_table_id
}) {
  try {
    const body = { name, clone_from_project_id, clone_from_table_id };
    const { data, status } = await apiClient.post("/me/templates", body);
    if (status === 201) return data; // «Template created successfully»
    throw new Error(data?.error || "Error creating the template.");
  } catch (error) {
    throw new Error(
      error.response?.data?.error || "Error creating the template."
    );
  }
}

/*--------------------------------------------------
  Endpoint para el provisionamiento del usuario
--------------------------------------------------*/
export async function getProfileAPI() {
  try {
    const { data } = await apiClient.get("/me/profile");
    return data;
  } catch (error) {
    throw new Error(
      error.response?.data?.error || "Error retrieving the user profile."
    );
  }
}

/*--------------------------------------------------
  Endpoints de pagos
--------------------------------------------------*/
export async function createCheckoutSession() {
  const { data } = await apiClient.get("/me/checkout-session");
  return data;
}

export async function getCustomerPortalUrl() {
  const { data } = await apiClient.get("/me/customer-portal-url");
  return data;
}

/*--------------------------------------------------
  Endpoint de creación de proyecto
--------------------------------------------------*/
const MAX_FILE_MB = 25;

export async function createProjectAPI({ name, file }) {
  if (!file) throw new Error("No file uploaded");
  if (file.size / 1024 / 1024 > MAX_FILE_MB)
    throw new Error("File too large. Max allowed size is 25 MB");

  const formData = new FormData();
  formData.append("name", name);
  formData.append("file", file);

  try {
    const { data, status } = await apiClient.post("/me/projects", formData, {
      headers: { "Content-Type": "multipart/form-data" },
      validateStatus: (s) => s < 500
    });

    if (status === 201) return data;
    throw new Error(data.error || "Unknown error creating the project.");
  } catch (err) {
    throw err instanceof Error ? err : new Error(String(err));
  }
}

/*--------------------------------------------------
  Endpoint para listar proyectos del usuario
--------------------------------------------------*/
export async function getProjectsAPI(limit = 20, before) {
  const params = new URLSearchParams({ limit });
  if (before) params.append("before", before);

  try {
    const { data } = await apiClient.get(`/me/projects?${params.toString()}`);
    return data;
  } catch (error) {
    throw new Error(
      error.response?.data?.error || "Error retrieving the projects."
    );
  }
}

/*--------------------------------------------------
  Detalle de un proyecto (PDF, tablas, etc.)
--------------------------------------------------*/
export async function getProjectDetailsAPI(projectId) {
  try {
    const { data } = await apiClient.get(`/me/projects/${projectId}`);
    return data;
  } catch (error) {
    throw new Error(
      error.response?.data?.error || "Error retrieving the project details."
    );
  }
}

/*--------------------------------------------------
  Listado de tablas de un proyecto
--------------------------------------------------*/
export async function getProjectTablesAPI(projectId) {
  try {
    const { data } = await apiClient.get(`/me/projects/${projectId}/tables`);
    return data.tables;
  } catch (error) {
    throw new Error(
      error.response?.data?.error || "Error retrieving the project's tables."
    );
  }
}

/*--------------------------------------------------
  Crear una tabla en un proyecto
--------------------------------------------------*/
export async function createProjectTableAPI(projectId, body) {
  try {
    const { data, status } = await apiClient.post(
      `/me/projects/${projectId}/tables`,
      body
    );
    if (status === 201) return data.table;
    throw new Error(data.error || "Error creating table");
  } catch (error) {
    throw new Error(error.response?.data?.error || "Error creating the table");
  }
}

/*--------------------------------------------------
  Actualizar nombre de tabla
--------------------------------------------------*/
export async function updateTableNameAPI(projectId, tableId, newName) {
  try {
    const { data, status } = await apiClient.put(
      `/me/projects/${projectId}/tables/${tableId}`,
      { name: newName }
    );
    if (status === 200) return data;
    throw new Error(data.error || "Error updating the table name.");
  } catch (err) {
    throw new Error(err.response?.data?.error || "Error renaming the table.");
  }
}

/*--------------------------------------------------
  Eliminar tabla de un proyecto
--------------------------------------------------*/
export async function deleteProjectTableAPI(projectId, tableId) {
  try {
    const { status } = await apiClient.delete(
      `/me/projects/${projectId}/tables/${tableId}`
    );
    if (status === 200) return true;
    throw new Error("Error deleting the table.");
  } catch (error) {
    throw new Error(error.response?.data?.error || "Error deleting the table.");
  }
}

/*--------------------------------------------------
  Endpoint para actualizar un proyecto
--------------------------------------------------*/
export async function updateProjectAPI(projectId, body) {
  try {
    const { data, status } = await apiClient.put(
      `/me/projects/${projectId}`,
      body,
      { headers: { "Content-Type": "application/json" } }
    );
    if (status === 200) return data;
    throw new Error(data.error || "Error updating the project.");
  } catch (err) {
    throw err instanceof Error ? err : new Error(String(err));
  }
}

/*--------------------------------------------------
  Endpoint para eliminar un proyecto
--------------------------------------------------*/
export async function deleteProjectAPI(projectId) {
  try {
    const { data, status } = await apiClient.delete(`/me/projects/${projectId}`);
    if (status === 200) return data;
    throw new Error(data.error || "Error deleting the project.");
  } catch (err) {
    throw err instanceof Error ? err : new Error(String(err));
  }
}

/*--------------------------------------------------
  Endpoints para chat
--------------------------------------------------*/
export async function createChatSessionAPI(projectId) {
  const { data } = await apiClient.post(`/me/projects/${projectId}/chat-sessions`);
  return data;
}

export async function getChatSessionAPI(
  projectId,
  chatSessionId,
  limit = 20,
  before = null
) {
  const qs = new URLSearchParams({ limit });
  if (before) qs.append("before", before);
  const { data } = await apiClient.get(
    `/me/projects/${projectId}/chat-sessions/${chatSessionId}?${qs.toString()}`
  );
  return data;
}

export async function getChatCitationAPI(
  projectId,
  chatSessionId,
  citationId,
  run_id
) {
  if (!run_id) {
    throw new Error("getChatCitationAPI requires a valid 'run_id'");
  }

  const url = `/me/projects/${projectId}/chat-sessions/${chatSessionId}/messages/citation-content/${citationId}?run_id=${encodeURIComponent(run_id)}`;

  const { data } = await apiClient.get(url);
  return data;
}

export async function cancelChatRunAPI(
  projectId,
  chatSessionId,
  threadId
) {
  console.log("canceling")
  if (!threadId) {
    throw new Error("cancelChatRun requires a valid 'thread_id'")
  }

  const url = `/me/projects/${projectId}/chat-sessions/${chatSessionId}/threads/${threadId}/runs/cancel`

  const { data } = await apiClient.post(url);
  return data;
}


export default apiClient;

/*--------------------------------------------------
  FILAS: crear, actualizar y eliminar
--------------------------------------------------*/
export async function createRowAPI(projectId, tableId, rowBody) {
  const { data, status } = await apiClient.post(
    `/me/projects/${projectId}/tables/${tableId}/rows`,
    { row: rowBody },
    { headers: { "Content-Type": "application/json" } }
  );
  if (status === 201 && data.row) return data.row;
  throw new Error(data?.error || data?.message || "Error al crear la fila");
}

export async function updateRowAPI(projectId, tableId, rowId, rowBody) {
  const { data, status } = await apiClient.put(
    `/me/projects/${projectId}/tables/${tableId}/rows/${rowId}`,
    { row: rowBody },
    { headers: { "Content-Type": "application/json" } }
  );
  if (status >= 200 && status < 300 && data.row) return data.row;
  throw new Error(data?.error || data?.message || "Error al actualizar la fila");
}

export async function deleteRowAPI(projectId, tableId, rowId) {
  const { status } = await apiClient.delete(
    `/me/projects/${projectId}/tables/${tableId}/rows/${rowId}`
  );
  if (status === 200) return true;
  throw new Error("Error deleting the row.");
}


/*--------------------------------------------------
  Iniciar extracción de datos de una tabla
--------------------------------------------------*/
export async function startDataExtractionAPI(projectId, tableId) {
  const { data, status } = await apiClient.post(
    `/me/projects/${projectId}/tables/${tableId}/extract`
  );

  /* ✔️ Consideramos éxito cualquier 2xx (200, 202, etc.)               */
  if (status >= 200 && status < 300) return data;

  /* ❌ Cualquier otro código se trata como error                       */
  throw new Error(data?.error || "Error starting the extraction.");
}


/*--------------------------------------------------
  Obtener una tabla concreta (incluye filas y estado)
--------------------------------------------------*/
export async function getProjectTableAPI(projectId, tableId) {
  const { data, status } = await apiClient.get(
    `/me/projects/${projectId}/tables/${tableId}`
  );

  if (status !== 200) {
    throw new Error(data?.error || "Error retrieving the table.");
  }

  /* El backend puede responder:
       { table: { … } }   ó   { id, rows, extraction_status, … }
     ⇒ normalizamos devolviendo siempre el objeto-tabla           */
  if (data.table) return data.table;
  return data;                  // ya es la tabla
}

//delete templates

export async function deleteTemplateAPI(templateId) {
  try {
    const { status } = await apiClient.delete(`/me/templates/${templateId}`);
    if (status === 200) return true;
    throw new Error("Error deleting the template.");
  } catch (error) {
    throw new Error(
      error.response?.data?.error || "Error deleting the template."
    );
  }
}

/*--------------------------------------------------
  Feedback de extracción
--------------------------------------------------*/
export async function sendExtractionFeedbackAPI({
    extraction_row_id,
    user_feedback_comment,
    is_liked,
    is_disliked,
    not_like_not_dislike,
  }) {
    try {
      const body = {
        extraction_row_id,
        user_feedback_comment,
        is_liked,
        is_disliked,
        not_like_not_dislike,
      };
  
      const { status } = await apiClient.post(
        "/me/extraction-feedbacks",
        body,
        { headers: { "Content-Type": "application/json" } },
      );
  
      if (status === 200) return true;
      throw new Error("Unexpected status when sending feedback");
    } catch (error) {
      throw new Error(
        error.response?.data?.error || "Error submitting your feedback.",
      );
    }
  }